import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { QuoteContentTemplateApiService } from '../services/quote-content-template-api.service';
import {
  QuoteContentTemplateRequest,
  QuoteContentTemplateResponse,
  QuoteContentTemplateItemResponse,
  QuoteContentTemplateItemType
} from '../models/quote-content-template.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ProductoApiService } from '../../productos/services/producto-api.service';
import { ProductResponse } from '../../productos/models/producto.model';
import { ProductKind } from '../quotes-form/quotes-form.component';

@Component({
  selector: 'app-quote-content-template-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent
  ],
  templateUrl: './quote-content-template-form.component.html'
})
export class QuoteContentTemplateFormComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  templateId: number | null = null;

  // ------------------------------------------------------------
  // Catálogo de productos/servicios para las líneas de la plantilla
  // ------------------------------------------------------------
  // Antes el "Tipo" era un <select> suelto (Personalizado/Producto/Paquete) SIN ningún selector de
  // catálogo: aunque el modelo ya guardaba productId, el formulario nunca lo pedía, así que hasta
  // los productos que ya existen había que teclearlos a mano (nombre, precio, unidad). Ahora la
  // línea usa el MISMO patrón que el paso 2 del wizard —Productos (Con/Sin Inventario), Servicios
  // (Recursos/Consultoría) y Personalizado— con buscador filtrado por ese tipo.
  products: ProductResponse[] = [];
  loadingProducts = false;
  /** Subtipo de "Productos" por línea (Con Inventario por defecto). Filtro de catálogo, no viaja al backend. */
  itemProductKinds: Record<number, ProductKind> = {};
  /** Texto tecleado en el buscador de producto de cada línea. */
  itemProductSearchTerms: Record<number, string> = {};
  showItemProductDropdown: Record<number, boolean> = {};

  constructor(
    private fb: FormBuilder,
    private templateApi: QuoteContentTemplateApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
    private productoApi: ProductoApiService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.cargarProductos();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.templateId = +id;
      this.cargarTemplate(this.templateId);
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(150)]],
      description: ['', Validators.maxLength(2000)],
      defaultTermsConditions: ['', Validators.maxLength(5000)],
      defaultNotes: ['', Validators.maxLength(2000)],
      approvalRequired: [false],
      isActive: [true],
      items: this.fb.array([])
    });
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray;
  }

  private createItem(item?: QuoteContentTemplateItemResponse): FormGroup {
    return this.fb.group({
      itemName: [item?.itemName || '', [Validators.required, Validators.maxLength(200)]],
      description: [item?.description || '', Validators.maxLength(2000)],
      quantity: [item?.quantity ?? 1, [Validators.required, Validators.min(0.01)]],
      unitPrice: [item?.unitPrice ?? 0, [Validators.required, Validators.min(0)]],
      unit: [item?.unit || '', Validators.maxLength(50)],
      itemType: [item?.itemType || 'custom'],
      productId: [item?.productId ?? null]
    });
  }

  addItem(): void {
    this.items.push(this.createItem());
  }

  removeItem(index: number): void {
    this.items.removeAt(index);
    // Los índices de las líneas siguientes se corren: descartar el estado local por índice para
    // que el buscador de una línea no herede el término/subtipo de la que se borró.
    this.itemProductKinds = {};
    this.itemProductSearchTerms = {};
    this.showItemProductDropdown = {};
    this.items.controls.forEach((c, i) => {
      this.itemProductSearchTerms[i] = this.productLabelFor(c.get('productId')?.value);
    });
  }

  // ------------------------------------------------------------
  // Selector de producto/servicio del catálogo por línea
  // ------------------------------------------------------------

  private cargarProductos(): void {
    this.loadingProducts = true;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.productoApi.searchProduct({ businessId, isActive: true, page: 0, size: 1000 }).subscribe({
      next: (res) => {
        this.products = res?.content || [];
        this.loadingProducts = false;
        // El catálogo puede llegar después de cargarTemplate(): re-etiquetar los buscadores.
        this.items.controls.forEach((c, i) => {
          if (!this.itemProductSearchTerms[i]) {
            this.itemProductSearchTerms[i] = this.productLabelFor(c.get('productId')?.value);
          }
        });
      },
      // Sin catálogo el formulario sigue sirviendo: sólo quedan las líneas personalizadas.
      error: () => { this.loadingProducts = false; }
    });
  }

  /** ¿El tipo de la línea admite elegir un producto del catálogo? (Productos y Servicios). */
  isProductLikeType(type: unknown): boolean {
    return type === 'product' || type === 'service_resource' || type === 'service_consulting';
  }

  isServiceType(type: unknown): boolean {
    return type === 'service_resource' || type === 'service_consulting';
  }

  productKindFor(index: number): ProductKind {
    const explicit = this.itemProductKinds[index];
    if (explicit) return explicit;
    const productId = this.items.at(index)?.get('productId')?.value;
    const product = productId ? this.products.find(p => p.id === Number(productId)) : null;
    return (product?.type || '').toLowerCase() === 'no_inventory' ? 'no_inventory' : 'inventory';
  }

  setProductKindFor(index: number, kind: ProductKind): void {
    if (this.productKindFor(index) === kind) return;
    this.itemProductKinds[index] = kind;
    this.limpiarProductoDeLinea(index);
  }

  /** Cambia el tipo de la línea y descarta el producto elegido bajo el tipo anterior. */
  setItemType(index: number, type: 'custom' | 'product' | 'service_resource' | 'service_consulting'): void {
    const ctrl = this.items.at(index)?.get('itemType');
    if (!ctrl || ctrl.value === type) return;
    ctrl.setValue(type);
    this.limpiarProductoDeLinea(index);
  }

  /** Botón "Servicios": entra por Recursos, como en el paso 2 del wizard. */
  selectServiceType(index: number): void {
    if (this.isServiceType(this.items.at(index)?.get('itemType')?.value)) return;
    this.setItemType(index, 'service_resource');
  }

  private limpiarProductoDeLinea(index: number): void {
    this.items.at(index)?.patchValue({ productId: null });
    this.itemProductSearchTerms[index] = '';
    this.showItemProductDropdown[index] = false;
  }

  onProductSearchInput(index: number, term: string): void {
    this.itemProductSearchTerms[index] = term;
    this.showItemProductDropdown[index] = true;
  }

  onProductSearchEnter(index: number, event: Event): void {
    // Sin preventDefault, Enter dentro del <form> dispara ngSubmit y guarda a medio llenar.
    event.preventDefault();
    this.showItemProductDropdown[index] = true;
  }

  closeProductDropdown(index: number): void {
    setTimeout(() => { this.showItemProductDropdown[index] = false; }, 200);
  }

  /**
   * Productos del catálogo que corresponden al tipo/subtipo de la línea, filtrados por el término.
   * Mismo criterio que el paso 2 del wizard (INC_CRM_005 (5) inc41/42), incluida la tolerancia con
   * los servicios sin subtipo y los productos con `type` desconocido.
   */
  filteredProductsFor(index: number): ProductResponse[] {
    const itemType = this.items.at(index)?.get('itemType')?.value;
    const kind = this.productKindFor(index);
    const base = this.products.filter(p => this.matchesItemType(p, itemType, kind));
    const term = (this.itemProductSearchTerms[index] || '').trim().toLowerCase();
    if (!term) return base.slice(0, 30);
    return base.filter(p =>
      p.name.toLowerCase().includes(term) || (p.sku || '').toLowerCase().includes(term));
  }

  private matchesItemType(p: ProductResponse, itemType: string, kind: ProductKind): boolean {
    const type = (p.type || '').toLowerCase();
    const subtype = (p.subtype || '').toLowerCase();
    if (itemType === 'service_resource') return type === 'service' && subtype !== 'consulting';
    if (itemType === 'service_consulting') return type === 'service' && subtype === 'consulting';
    if (itemType === 'product') {
      if (type === 'service') return false;
      return kind === 'no_inventory' ? type === 'no_inventory' : type !== 'no_inventory';
    }
    return false;
  }

  /**
   * Elegir un producto autocompleta la línea con los datos del catálogo (nombre, descripción,
   * precio y unidad). Todo queda editable: la plantilla puede fijar un precio pactado distinto
   * del de lista sin perder el vínculo con el producto (productId).
   */
  selectProductForItem(index: number, product: ProductResponse): void {
    const group = this.items.at(index);
    if (!group) return;
    group.patchValue({
      productId: product.id,
      itemName: product.name,
      description: product.description || '',
      unitPrice: product.price ?? 0,
      unit: group.get('unit')?.value || product.unitOfMeasure || ''
    });
    this.itemProductSearchTerms[index] = this.productLabel(product);
    this.showItemProductDropdown[index] = false;
  }

  /** Quita el vínculo con el catálogo dejando lo ya escrito: la línea pasa a ser libre. */
  clearProductForItem(index: number): void {
    this.items.at(index)?.patchValue({ productId: null });
    this.itemProductSearchTerms[index] = '';
  }

  productLabel(p: ProductResponse): string {
    return p.name + (p.sku ? ' — ' + p.sku : '');
  }

  private productLabelFor(productId: unknown): string {
    const p = productId ? this.products.find(x => x.id === Number(productId)) : null;
    return p ? this.productLabel(p) : '';
  }

  cargarTemplate(id: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.templateApi.getTemplateById(id, businessId).subscribe({
      next: (template: QuoteContentTemplateResponse) => {
        this.form.patchValue({
          name: template.name,
          description: template.description || '',
          defaultTermsConditions: template.defaultTermsConditions || '',
          defaultNotes: template.defaultNotes || '',
          approvalRequired: template.approvalRequired ?? false,
          isActive: template.isActive ?? true
        });
        this.items.clear();
        (template.items || [])
          .sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0))
          .forEach((item) => this.items.push(this.createItem(item)));
        // Etiquetar el buscador de cada línea con el producto ya vinculado (si el catálogo llegó).
        this.items.controls.forEach((c, i) => {
          this.itemProductSearchTerms[i] = this.productLabelFor(c.get('productId')?.value);
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la plantilla';
        this.loading = false;
      }
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const formValue = this.form.value;

    const templateRequest: QuoteContentTemplateRequest = {
      name: formValue.name,
      description: formValue.description || undefined,
      defaultTermsConditions: formValue.defaultTermsConditions || undefined,
      defaultNotes: formValue.defaultNotes || undefined,
      approvalRequired: formValue.approvalRequired,
      isActive: formValue.isActive,
      items: (formValue.items as Array<Record<string, unknown>>).map((item, index) => ({
        itemOrder: index + 1,
        itemName: item['itemName'] as string,
        description: (item['description'] as string) || undefined,
        quantity: Number(item['quantity']),
        unitPrice: Number(item['unitPrice']),
        unit: (item['unit'] as string) || undefined,
        itemType: (item['itemType'] as QuoteContentTemplateItemType) || 'custom',
        // Sólo las líneas ligadas al catálogo llevan productId; en 'custom' debe ir ausente
        // (el backend rechaza un ítem 'custom' con producto al generar la cotización).
        productId: this.isProductLikeType(item['itemType']) ? ((item['productId'] as number) ?? undefined) : undefined
      }))
    };

    const request$ = this.isEditing && this.templateId
      ? this.templateApi.updateTemplate(this.templateId, businessId, templateRequest)
      : this.templateApi.createTemplate(businessId, templateRequest);

    request$.subscribe({
      next: () => {
        this.saving = false;
        if (this.isEditing) {
          this.toast.updated('Plantilla de contenido');
        } else {
          this.toast.created('Plantilla de contenido');
        }
        this.router.navigate(['/cotizaciones/plantillas-contenido']);
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar la plantilla';
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  cancelar(): void {
    this.router.navigate(['/cotizaciones/plantillas-contenido']);
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  itemHasError(index: number, field: string): boolean {
    const control = this.items.at(index)?.get(field);
    return control ? control.invalid && control.touched : false;
  }
}
